/**
 * persist.ts
 *
 * 瀏覽器持久化存儲管理。
 *
 * 使用 Storage API (navigator.storage) 申請持久化存儲權限，
 * 避免瀏覽器在磁盤空間不足時自動清除 IndexedDB 資料。
 *
 * 持久化存儲申請後，資料只有在用戶主動清除網站資料時才會消失，
 * 這對於儲存未同步的離線操作（outbox）至關重要。
 */

/**
 * 持久化存儲狀態。
 */
export interface PersistStatus {
  /** 是否已取得持久化存儲權限 */
  persisted: boolean;
  /** 當前存儲使用量佔配額的百分比（0–100），null 表示不支持查詢 */
  quotaPct: number | null;
}

/**
 * 申請持久化存儲（Persistent Storage）。
 *
 * - 若瀏覽器不支持 Storage API，直接返回 false
 * - 若已持久化，直接返回 true（不重複申請）
 * - 否則呼叫 navigator.storage.persist() 請求權限
 *
 * 注意：Chrome 通常需要用戶已「添加到主屏幕」或站點在書籤中才會自動授予，
 * 否則需要彈窗詢問用戶。
 *
 * @returns 是否成功取得持久化存儲權限
 */
export async function ensurePersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  const already = await navigator.storage.persisted();
  if (already) return true;
  return await navigator.storage.persist();
}

/**
 * 查詢當前存儲狀態（持久化狀態 + 使用量百分比）。
 *
 * 使用 StorageManager.estimate() 取得配額和用量資訊。
 * 各瀏覽器的 quota 估算方式不同，數值僅供參考。
 *
 * @returns PersistStatus 物件
 */
export async function getStorageStatus(): Promise<PersistStatus> {
  // 查詢持久化狀態
  const persisted = (await navigator.storage?.persisted?.()) ?? false;

  // 查詢存儲配額使用情況
  let quotaPct: number | null = null;
  if (navigator.storage?.estimate) {
    const est = await navigator.storage.estimate();
    if (est.quota && est.usage != null) {
      quotaPct = (est.usage / est.quota) * 100;
    }
  }

  return { persisted, quotaPct };
}
